import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Form, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import classnames from "classnames";
import Flatpickr from "react-flatpickr";

//import images
import progileBg from '../../../../assets/images/profile-bg.jpg';
import avatar1 from '../../../../assets/images/users/avatar-1.jpg';

// Skills Tag Input Component
interface SkillsTagInputProps {
    skills: string[];
    onChange: (skills: string[]) => void;
}

const SkillsTagInput: React.FC<SkillsTagInputProps> = ({ skills, onChange }) => {
    const [inputValue, setInputValue] = useState('');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            if (!skills.includes(inputValue.trim())) {
                onChange([...skills, inputValue.trim()]);
                setInputValue('');
            }
        }
    };

    const removeSkill = (skillToRemove: string) => {
        onChange(skills.filter(skill => skill !== skillToRemove));
    };

    return (
        <div className="border rounded p-2" style={{ minHeight: '60px' }}>
            <div className="d-flex flex-wrap gap-2 align-items-center">
                {skills.map((skill, index) => (
                    <div
                        key={index}
                        className="badge bg-primary-subtle text-primary d-flex align-items-center gap-1 px-2 py-1"
                        style={{ fontSize: '0.875rem' }}
                    >
                        <span>{skill}</span>
                        <button
                            type="button"
                            className="btn-close btn-close-sm"
                            style={{ fontSize: '0.65rem', padding: '0.1rem' }}
                            onClick={() => removeSkill(skill)}
                            aria-label="Remove skill"
                        />
                    </div>
                ))}
                <input
                    type="text"
                    className="border-0 flex-grow-1"
                    placeholder={skills.length === 0 ? "Type a skill and press Enter..." : "Add more..."}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                        outline: 'none',
                        minWidth: '150px',
                        background: 'transparent'
                    }}
                />
            </div>
        </div>
    );
};

const Settings = () => {
    const [activeTab, setActiveTab] = useState("1");
    const [profileImage, setProfileImage] = useState(avatar1);
    const [user, setUser] = useState<any>(null);

    // Profile form state
    const [profileData, setProfileData] = useState({
        name: '',
        phoneNumber: '',
        joiningDate: null as Date | null,
        designation: '',
        website: '',
        bio: ''
    });
    const [skills, setSkills] = useState<string[]>([]);

    React.useEffect(() => {
        const loadProfileData = async () => {
            const authUser = sessionStorage.getItem("authUser");
            if (!authUser) return;

            const obj = JSON.parse(authUser);
            const token = obj.token;

            try {
                // Fetch complete user profile from API
                const profileResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.atoscloud.app'}/api/users/${obj.user.id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (profileResponse.ok) {
                    const userData = await profileResponse.json();
                    setUser(userData);
                    setProfileImage(userData.avatarUrl || avatar1);

                    // Initialize form data from API response
                    setProfileData({
                        name: userData.name || '',
                        phoneNumber: userData.phoneNumber || '',
                        joiningDate: userData.joiningDate ? new Date(userData.joiningDate) : null,
                        designation: userData.designation || '',
                        website: userData.website || '',
                        bio: userData.bio || ''
                    });
                } else {
                    // Fallback to sessionStorage if API fails
                    setUser(obj.user);
                    setProfileImage(obj.user?.avatarUrl || avatar1);
                    setProfileData({
                        name: obj.user?.name || '',
                        phoneNumber: obj.user?.phoneNumber || '',
                        joiningDate: obj.user?.joiningDate ? new Date(obj.user.joiningDate) : null,
                        designation: obj.user?.designation || '',
                        website: obj.user?.website || '',
                        bio: obj.user?.bio || ''
                    });
                }

                // Fetch skills from API
                const skillsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.atoscloud.app'}/api/profile/skills`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (skillsResponse.ok) {
                    const skillsData = await skillsResponse.json();
                    // Extract skill names from the response
                    const skillNames = skillsData.map((skill: any) => skill.skillName || skill.name);
                    setSkills(skillNames);
                }
            } catch (error) {
                console.error('Error loading profile data:', error);
                // Fallback to sessionStorage on error
                setUser(obj.user);
                setProfileImage(obj.user?.avatarUrl || avatar1);
            }
        };

        loadProfileData();
    }, []);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64Image = reader.result as string;
                setProfileImage(base64Image);

                // Update avatar in backend
                if (user?.id) {
                    try {
                        const authUser = sessionStorage.getItem("authUser");
                        const token = authUser ? JSON.parse(authUser).token : null;
                        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.atoscloud.app'}/api/users/${user.id}/avatar`, {
                            method: 'PATCH',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ avatarUrl: base64Image })
                        });

                        if (response.ok) {
                            // Update sessionStorage
                            const authUser = sessionStorage.getItem("authUser");
                            if (authUser) {
                                const authData = JSON.parse(authUser);
                                authData.user.avatarUrl = base64Image;
                                sessionStorage.setItem("authUser", JSON.stringify(authData));
                            }
                        }
                    } catch (error) {
                        console.error("Error updating avatar:", error);
                    }
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const tabChange = (tab : any) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const handleUpdateProfile = async () => {
        try {
            const authUser = sessionStorage.getItem("authUser");
            const token = authUser ? JSON.parse(authUser).token : null;

            // Use the batch endpoint for atomic updates
            const batchData = {
                ...profileData,
                skills: skills.length > 0 ? skills : null,
                primaryAddress: null, // Add address if needed
                experiences: null     // Add experiences if needed
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.atoscloud.app'}/api/profile/batch`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(batchData)
            });

            if (response.ok) {
                alert('Profile updated successfully!');

                // Update sessionStorage
                if (authUser) {
                    const obj = JSON.parse(authUser);
                    obj.user = { ...obj.user, ...profileData };
                    sessionStorage.setItem("authUser", JSON.stringify(obj));
                    setUser(obj.user);
                }
            } else {
                const error = await response.text();
                alert(`Failed to update profile: ${error}`);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('An error occurred while updating your profile');
        }
    };

    document.title = "Profile Settings | ProMédico Contabilidade";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div className="position-relative mx-n4 mt-n4">
                        <div className="profile-wid-bg profile-setting-img">
                            <img src={progileBg} className="profile-wid-img" alt="" />
                            <div className="overlay-content">
                                <div className="text-end p-3">
                                    <div className="p-0 ms-auto rounded-circle profile-photo-edit">
                                        <Input id="profile-foreground-img-file-input" type="file"
                                            className="profile-foreground-img-file-input" />
                                        <Label htmlFor="profile-foreground-img-file-input"
                                            className="profile-photo-edit btn btn-light">
                                            <i className="ri-image-edit-line align-bottom me-1"></i> Change Cover
                                        </Label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Row>
                        <Col xxl={3}>
                            <Card className="mt-n5">
                                <CardBody className="p-4">
                                    <div className="text-center">
                                        <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                                            <img src={profileImage}
                                                className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                                                alt="user-profile" />
                                            <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                                                <Input id="profile-img-file-input" type="file"
                                                    className="profile-img-file-input"
                                                    accept="image/*"
                                                    onChange={handleImageChange} />
                                                <Label htmlFor="profile-img-file-input"
                                                    className="profile-photo-edit avatar-xs">
                                                    <span className="avatar-title rounded-circle bg-light text-body">
                                                        <i className="ri-camera-fill"></i>
                                                    </span>
                                                </Label>
                                            </div>
                                        </div>
                                        <h5 className="fs-16 mb-1">{user?.name}</h5>
                                        <p className="text-muted mb-0">{user?.email}</p>
                                        <p className="text-muted mb-0"><span className="badge badge-soft-success">{user?.role}</span></p>
                                    </div>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardBody>
                                    <div className="d-flex align-items-center mb-5">
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-0">Complete Your Profile</h5>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Link to="#" className="badge bg-light text-primary fs-12"><i
                                                className="ri-edit-box-line align-bottom me-1"></i> Edit</Link>
                                        </div>
                                    </div>
                                    <div className="progress animated-progress custom-progress progress-label">
                                        <div className="progress-bar bg-danger" role="progressbar" style={{ "width": "30%" }}>
                                            <div className="label">30%</div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-0">Portfolio</h5>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Link to="#" className="badge bg-light text-primary fs-12"><i
                                                className="ri-add-fill align-bottom me-1"></i> Add</Link>
                                        </div>
                                    </div>
                                    <div className="mb-3 d-flex">
                                        <div className="avatar-xs d-block flex-shrink-0 me-3">
                                            <span className="avatar-title rounded-circle fs-16 bg-dark text-light">
                                                <i className="ri-github-fill"></i>
                                            </span>
                                        </div>
                                        <Input type="email" className="form-control" id="gitUsername" placeholder="Username"
                                            defaultValue="@daveadame" />
                                    </div>
                                    <div className="mb-3 d-flex">
                                        <div className="avatar-xs d-block flex-shrink-0 me-3">
                                            <span className="avatar-title rounded-circle fs-16 bg-primary">
                                                <i className="ri-global-fill"></i>
                                            </span>
                                        </div>
                                        <Input type="text" className="form-control" id="websiteInput"
                                            placeholder="www.example.com" defaultValue="www.velzon.com" />
                                    </div>
                                    <div className="mb-3 d-flex">
                                        <div className="avatar-xs d-block flex-shrink-0 me-3">
                                            <span className="avatar-title rounded-circle fs-16 bg-success">
                                                <i className="ri-dribbble-fill"></i>
                                            </span>
                                        </div>
                                        <Input type="text" className="form-control" id="dribbleName" placeholder="Username"
                                            defaultValue="@dave_adame" />
                                    </div>
                                    <div className="d-flex">
                                        <div className="avatar-xs d-block flex-shrink-0 me-3">
                                            <span className="avatar-title rounded-circle fs-16 bg-danger">
                                                <i className="ri-pinterest-fill"></i>
                                            </span>
                                        </div>
                                        <Input type="text" className="form-control" id="pinterestName"
                                            placeholder="Username" defaultValue="Advance Dave" />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xxl={9}>
                            <Card className="mt-xxl-n5">
                                <CardHeader>
                                    <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                                        role="tablist">
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === "1" })}
                                                onClick={() => {
                                                    tabChange("1");
                                                }}>
                                                <i className="fas fa-home"></i>
                                                Personal Details
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink to="#"
                                                className={classnames({ active: activeTab === "2" })}
                                                onClick={() => {
                                                    tabChange("2");
                                                }}
                                                type="button">
                                                <i className="far fa-user"></i>
                                                Change Password
                                            </NavLink>
                                        </NavItem>
                                        <NavItem >
                                            <NavLink to="#"
                                                className={classnames({ active: activeTab === "3" })}
                                                onClick={() => {
                                                    tabChange("3");
                                                }}
                                                type="button">
                                                <i className="far fa-envelope"></i>
                                                Experience
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </CardHeader>
                                <CardBody className="p-4">
                                    <TabContent activeTab={activeTab}>
                                        <TabPane tabId="1">
                                            <Form>
                                                <Row>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="nameInput" className="form-label">Full Name</Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="nameInput"
                                                                placeholder="Enter your full name"
                                                                value={profileData.name || ''}
                                                                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="phonenumberInput" className="form-label">Phone Number</Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="phonenumberInput"
                                                                placeholder="Enter your phone number"
                                                                value={profileData.phoneNumber || ''}
                                                                onChange={(e) => setProfileData({ ...profileData, phoneNumber: e.target.value })}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="JoiningdatInput" className="form-label">Joining Date</Label>
                                                            <Flatpickr
                                                                className="form-control"
                                                                value={profileData.joiningDate || undefined}
                                                                onChange={([date]) => {
                                                                    requestAnimationFrame(() => {
                                                                        setProfileData({ ...profileData, joiningDate: date });
                                                                    });
                                                                }}
                                                                options={{
                                                                    dateFormat: "d M, Y"
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="skillsInput" className="form-label">Skills</Label>
                                                            <SkillsTagInput skills={skills} onChange={setSkills} />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="designationInput" className="form-label">Designation</Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="designationInput"
                                                                placeholder="Designation"
                                                                value={profileData.designation || ''}
                                                                onChange={(e) => setProfileData({ ...profileData, designation: e.target.value })}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="websiteInput1" className="form-label">Website</Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="websiteInput1"
                                                                placeholder="www.example.com"
                                                                value={profileData.website || ''}
                                                                onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="mb-3 pb-2">
                                                            <Label htmlFor="bioTextarea" className="form-label">Bio</Label>
                                                            <textarea
                                                                className="form-control"
                                                                id="bioTextarea"
                                                                rows={3}
                                                                value={profileData.bio || ''}
                                                                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                                                                placeholder="Tell us about yourself..."
                                                            ></textarea>
                                                        </div>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="hstack gap-2 justify-content-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={handleUpdateProfile}
                                                            >
                                                                Update
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-soft-success"
                                                                onClick={() => {
                                                                    // Reset form to original values
                                                                    if (user) {
                                                                        setProfileData({
                                                                            name: user.name || '',
                                                                            phoneNumber: user.phoneNumber || '',
                                                                            joiningDate: user.joiningDate ? new Date(user.joiningDate) : null,
                                                                            designation: user.designation || '',
                                                                            website: user.website || '',
                                                                            bio: user.bio || ''
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </TabPane>

                                        <TabPane tabId="2">
                                            <Form>
                                                <Row className="g-2">
                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="oldpasswordInput" className="form-label">Old
                                                                Password*</Label>
                                                            <Input type="password" className="form-control"
                                                                id="oldpasswordInput"
                                                                placeholder="Enter current password" />
                                                        </div>
                                                    </Col>

                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="newpasswordInput" className="form-label">New
                                                                Password*</Label>
                                                            <Input type="password" className="form-control"
                                                                id="newpasswordInput" placeholder="Enter new password" />
                                                        </div>
                                                    </Col>

                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="confirmpasswordInput" className="form-label">Confirm
                                                                Password*</Label>
                                                            <Input type="password" className="form-control"
                                                                id="confirmpasswordInput"
                                                                placeholder="Confirm password" />
                                                        </div>
                                                    </Col>

                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Link to="#"
                                                                className="link-primary text-decoration-underline">Forgot
                                                                Password ?</Link>
                                                        </div>
                                                    </Col>

                                                    <Col lg={12}>
                                                        <div className="text-end">
                                                            <button type="button" className="btn btn-success">Change
                                                                Password</button>
                                                        </div>
                                                    </Col>

                                                </Row>

                                            </Form>
                                            <div className="mt-4 mb-3 border-bottom pb-2">
                                                <div className="float-end">
                                                    <Link to="#" className="link-primary">All Logout</Link>
                                                </div>
                                                <h5 className="card-title">Login History</h5>
                                            </div>
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="flex-shrink-0 avatar-sm">
                                                    <div className="avatar-title bg-light text-primary rounded-3 fs-18">
                                                        <i className="ri-smartphone-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h6>iPhone 12 Pro</h6>
                                                    <p className="text-muted mb-0">Los Angeles, United States - March 16 at
                                                        2:47PM</p>
                                                </div>
                                                <div>
                                                    <Link to="#">Logout</Link>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="flex-shrink-0 avatar-sm">
                                                    <div className="avatar-title bg-light text-primary rounded-3 fs-18">
                                                        <i className="ri-tablet-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h6>Apple iPad Pro</h6>
                                                    <p className="text-muted mb-0">Washington, United States - November 06
                                                        at 10:43AM</p>
                                                </div>
                                                <div>
                                                    <Link to="#">Logout</Link>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="flex-shrink-0 avatar-sm">
                                                    <div className="avatar-title bg-light text-primary rounded-3 fs-18">
                                                        <i className="ri-smartphone-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h6>Galaxy S21 Ultra 5G</h6>
                                                    <p className="text-muted mb-0">Conneticut, United States - June 12 at
                                                        3:24PM</p>
                                                </div>
                                                <div>
                                                    <Link to="#">Logout</Link>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 avatar-sm">
                                                    <div className="avatar-title bg-light text-primary rounded-3 fs-18">
                                                        <i className="ri-macbook-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h6>Dell Inspiron 14</h6>
                                                    <p className="text-muted mb-0">Phoenix, United States - July 26 at
                                                        8:10AM</p>
                                                </div>
                                                <div>
                                                    <Link to="#">Logout</Link>
                                                </div>
                                            </div>
                                        </TabPane>

                                        <TabPane tabId="3">
                                            <form>
                                                <div id="newlink">
                                                    <div id="1">
                                                        <Row>
                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label htmlFor="jobTitle" className="form-label">Job
                                                                        Title</Label>
                                                                    <Input type="text" className="form-control"
                                                                        id="jobTitle" placeholder="Job title"
                                                                        defaultValue="Lead Designer / Developer" />
                                                                </div>
                                                            </Col>

                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label htmlFor="companyName" className="form-label">Company
                                                                        Name</Label>
                                                                    <Input type="text" className="form-control"
                                                                        id="companyName" placeholder="Company name"
                                                                        defaultValue="Themesbrand" />
                                                                </div>
                                                            </Col>

                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <label htmlFor="experienceYear"
                                                                        className="form-label">Experience Years</label>
                                                                    <Row>
                                                                        <Col lg={5}>
                                                                            <select className="form-control" data-choices
                                                                                data-choices-search-false
                                                                                name="experienceYear"
                                                                                id="experienceYear">
                                                                                <option defaultValue="">Select years</option>
                                                                                <option value="Choice 1">2001</option>
                                                                                <option value="Choice 2">2002</option>
                                                                                <option value="Choice 3">2003</option>
                                                                                <option value="Choice 4">2004</option>
                                                                                <option value="Choice 5">2005</option>
                                                                                <option value="Choice 6">2006</option>
                                                                                <option value="Choice 7">2007</option>
                                                                                <option value="Choice 8">2008</option>
                                                                                <option value="Choice 9">2009</option>
                                                                                <option value="Choice 10">2010</option>
                                                                                <option value="Choice 11">2011</option>
                                                                                <option value="Choice 12">2012</option>
                                                                                <option value="Choice 13">2013</option>
                                                                                <option value="Choice 14">2014</option>
                                                                                <option value="Choice 15">2015</option>
                                                                                <option value="Choice 16">2016</option>
                                                                                <option value="Choice 17" >2017</option>
                                                                                <option value="Choice 18">2018</option>
                                                                                <option value="Choice 19">2019</option>
                                                                                <option value="Choice 20">2020</option>
                                                                                <option value="Choice 21">2021</option>
                                                                                <option value="Choice 22">2022</option>
                                                                            </select>
                                                                        </Col>

                                                                        <div className="col-auto align-self-center">
                                                                            to
                                                                        </div>

                                                                        <Col lg={5}>
                                                                            <select className="form-control" data-choices
                                                                                data-choices-search-false
                                                                                name="choices-single-default2">
                                                                                <option defaultValue="">Select years</option>
                                                                                <option value="Choice 1">2001</option>
                                                                                <option value="Choice 2">2002</option>
                                                                                <option value="Choice 3">2003</option>
                                                                                <option value="Choice 4">2004</option>
                                                                                <option value="Choice 5">2005</option>
                                                                                <option value="Choice 6">2006</option>
                                                                                <option value="Choice 7">2007</option>
                                                                                <option value="Choice 8">2008</option>
                                                                                <option value="Choice 9">2009</option>
                                                                                <option value="Choice 10">2010</option>
                                                                                <option value="Choice 11">2011</option>
                                                                                <option value="Choice 12">2012</option>
                                                                                <option value="Choice 13">2013</option>
                                                                                <option value="Choice 14">2014</option>
                                                                                <option value="Choice 15">2015</option>
                                                                                <option value="Choice 16">2016</option>
                                                                                <option value="Choice 17">2017</option>
                                                                                <option value="Choice 18">2018</option>
                                                                                <option value="Choice 19">2019</option>
                                                                                <option value="Choice 20">2020</option>
                                                                                <option value="Choice 21">2021</option>
                                                                                <option value="Choice 22">2022</option>
                                                                            </select>
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            </Col>

                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label htmlFor="jobDescription" className="form-label">Job
                                                                        Description</Label>
                                                                    <Input type='textarea'
                                                                        className="form-control" id="jobDescription"
                                                                        rows="3"
                                                                        placeholder='Enter description'
                                                                        defaultValue="You always want to make sure that your fonts work well together and try to limit the number of fonts you use to three or less. Experiment and play around with the fonts that you already have in the software you're working with reputable font websites."
                                                                    />
                                                                </div>
                                                            </Col>

                                                            <div className="hstack gap-2 justify-content-end">
                                                                <Link className="btn btn-success"
                                                                    to="#">Delete</Link>
                                                            </div>
                                                        </Row>
                                                    </div>
                                                </div>
                                                <div id="newForm" style={{ "display": "none" }}>
                                                </div>

                                                <Col lg={12}>
                                                    <div className="hstack gap-2">
                                                        <button type="submit" className="btn btn-success">Update</button>
                                                        <Link to="#" className="btn btn-primary">Add
                                                            New</Link>
                                                    </div>
                                                </Col>
                                            </form>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Settings;